/**
 * Packs LightPaths into ONE static instance buffer (pure; no GL).
 *
 * Every segment of every path is one instance of a screen-space capsule:
 *
 *   [ax, ay, bx, by, mA, mB, path]      7 × float32 = 28 bytes
 *
 *   ax..by  Web-Mercator (0..1 world units) RELATIVE to a local origin (the site).
 *           Subtracting in float64 before the float32 store keeps full precision:
 *           a 130 km route spans ~3e-3 world units, where float32 resolves ~2 mm.
 *           The MVP is rebuilt around the same origin in float64 (matrix.ts).
 *   mA, mB  cumulative metres at A and B (monotonic along the path).
 *   path    row of the path in the state texture (state.ts).
 *
 * Closed paths (the site outline) get their closing segment appended when the
 * ring is not already closed, so a head of `lengthM` draws the whole ring.
 */
import type { LightPath } from "@/contracts/map";
import { along, bearing, cumulative, mercator, type LngLat } from "@/lib/geo";

export const INSTANCE_FLOATS = 7;
export const INSTANCE_BYTES = INSTANCE_FLOATS * 4;

export interface PackedPath {
  id: string;
  index: number; //         state-texture row
  kind: LightPath["kind"];
  closed: boolean;
  coords: LngLat[]; //      as drawn (closed rings include the closing vertex)
  cum: number[]; //         metres, same length as coords
  lengthM: number; //       drawn length (last cum)
  declaredLengthM: number; // the caller's lengthM (head ≥ this means "fully drawn")
  first: number; //         first instance
  count: number; //         instances (segments)
  bbox: [number, number, number, number]; // Mercator (absolute) w, n, e, s → [minX, minY, maxX, maxY]
}

export interface PackedPaths {
  origin: LngLat;
  originMerc: [number, number];
  data: Float32Array; //   instanceCount × INSTANCE_FLOATS
  instanceCount: number;
  paths: PackedPath[];
  byId: Map<string, PackedPath>;
}

const finite = (c: LngLat | undefined): c is LngLat => !!c && Number.isFinite(c[0]) && Number.isFinite(c[1]);

/** A usable cumulative-metres array for `coords`, or a freshly computed one. */
function cumFor(coords: LngLat[], cum: number[] | undefined): number[] {
  if (cum && cum.length === coords.length && cum[0] === 0) {
    let ok = true;
    for (let i = 1; i < cum.length; i++) {
      if (!(cum[i] >= cum[i - 1]) || !Number.isFinite(cum[i])) {
        ok = false;
        break;
      }
    }
    if (ok) return cum.slice();
  }
  return cumulative(coords);
}

/**
 * Pack paths around `origin` (lng/lat of the local origin; the site).
 * Invalid paths (fewer than two finite coordinates) are skipped. Duplicate ids: the last wins.
 */
export function packPaths(input: LightPath[], origin: LngLat): PackedPaths {
  const dedup = new Map<string, LightPath>();
  for (const p of input) if (p && p.id) dedup.set(p.id, p);

  const [ox, oy] = mercator(origin);
  const paths: PackedPath[] = [];
  const chunks: Float32Array[] = [];
  let total = 0;

  for (const p of dedup.values()) {
    let coords = (p.coords ?? []).filter(finite).map((c) => [c[0], c[1]] as LngLat);
    if (coords.length < 2) continue;
    let cum = cumFor(coords, coords.length === (p.coords ?? []).length ? p.cum : undefined);
    const closed = !!p.closed || p.kind === "site";
    if (closed) {
      const a = coords[0], b = coords[coords.length - 1];
      if (a[0] !== b[0] || a[1] !== b[1]) {
        coords = [...coords, [a[0], a[1]]];
        cum = cumFor(coords, undefined);
      }
    }
    const lengthM = cum[cum.length - 1];
    if (!(lengthM > 0)) continue;

    const merc = coords.map(mercator);
    const seg = new Float32Array((coords.length - 1) * INSTANCE_FLOATS);
    const index = paths.length;
    let n = 0;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of merc) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    for (let i = 0; i < coords.length - 1; i++) {
      const mA = cum[i], mB = cum[i + 1];
      if (mB - mA < 1e-3) continue; // duplicate vertex: nothing to draw, metres stay continuous
      const o = n * INSTANCE_FLOATS;
      seg[o] = merc[i][0] - ox;
      seg[o + 1] = merc[i][1] - oy;
      seg[o + 2] = merc[i + 1][0] - ox;
      seg[o + 3] = merc[i + 1][1] - oy;
      seg[o + 4] = mA;
      seg[o + 5] = mB;
      seg[o + 6] = index;
      n++;
    }
    if (!n) continue;
    const declared = Number.isFinite(p.lengthM) && p.lengthM > 0 ? p.lengthM : lengthM;
    paths.push({
      id: p.id,
      index,
      kind: p.kind,
      closed,
      coords,
      cum,
      lengthM,
      declaredLengthM: declared,
      first: total,
      count: n,
      bbox: [minX, minY, maxX, maxY],
    });
    chunks.push(seg.subarray(0, n * INSTANCE_FLOATS));
    total += n;
  }

  const data = new Float32Array(total * INSTANCE_FLOATS);
  let off = 0;
  for (const c of chunks) {
    data.set(c, off);
    off += c.length;
  }
  return {
    origin: [origin[0], origin[1]],
    originMerc: [ox, oy],
    data,
    instanceCount: total,
    paths,
    byId: new Map(paths.map((p) => [p.id, p])),
  };
}

/**
 * Instance ranges [first, count, first, count, …] covering `paths` (in pack order), merging
 * neighbours whose instances are contiguous. The renderer draws only these ranges: one lit hero
 * then costs its own ~400 segments instead of all ~5 600 (vertex work, both capsule passes).
 * Writes into `out` (reused across frames) and returns it.
 */
export function instanceRanges(paths: readonly PackedPath[], out: number[] = []): number[] {
  out.length = 0;
  let first = -1, end = -1;
  for (const p of paths) {
    if (p.count <= 0) continue;
    if (p.first === end) {
      end += p.count;
      continue;
    }
    if (first >= 0) out.push(first, end - first);
    first = p.first;
    end = p.first + p.count;
  }
  if (first >= 0) out.push(first, end - first);
  return out;
}

/** Caller metres → drawn metres (closed rings gain a closing segment; head = lengthM is always the end). */
export function toDrawnM(p: PackedPath, m: number): number {
  const k = p.declaredLengthM > 0 ? p.lengthM / p.declaredLengthM : 1;
  const d = m * k;
  return d <= 0 ? 0 : d >= p.lengthM ? p.lengthM : d;
}

/**
 * Point and travel bearing at `m` caller-metres along a packed path (binary search on cum).
 * Duplicate vertices never yield a meaningless bearing: the nearest real segment is used.
 */
export function pointAtPacked(p: PackedPath, m: number): { lngLat: LngLat; bearing: number } {
  const r = along(p.coords, p.cum, toDrawnM(p, m));
  let brg = r.bearing;
  const n = p.coords.length;
  let i = Math.min(Math.max(r.index, 0), n - 2);
  if (p.cum[i + 1] - p.cum[i] < 1e-3) {
    let j = i + 1;
    while (j < n - 1 && p.cum[j + 1] - p.cum[j] < 1e-3) j++;
    if (j < n - 1) i = j;
    else {
      j = i - 1;
      while (j >= 0 && p.cum[j + 1] - p.cum[j] < 1e-3) j--;
      i = Math.max(j, 0);
    }
    brg = bearing(p.coords[i], p.coords[i + 1]);
  }
  return { lngLat: [r.lngLat[0], r.lngLat[1]], bearing: brg };
}

/** Origin-relative Mercator of a lng/lat for this pack (float64 subtraction, then the caller stores float32). */
export function relMercator(pack: PackedPaths, c: LngLat): [number, number] {
  const [x, y] = mercator(c);
  return [x - pack.originMerc[0], y - pack.originMerc[1]];
}
